import { Component, Inject, OnDestroy, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AnnualPlanService } from '../../providers/annual-plans.services';
import { Subscription } from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialogActions, MatDialogClose,
  MatDialogContent, MatDialogModule,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnnualPlan } from "../../../common/models/annual-plan";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { TranslateModule } from "@ngx-translate/core";
import { MatError, MatFormField, MatFormFieldModule, MatLabel } from "@angular/material/form-field";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerModule,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import { MatInput, MatInputModule } from "@angular/material/input";
import { NgIf, NgFor } from "@angular/common";
import { MatButton } from "@angular/material/button";
import { DurationControlComponent } from "../../../common/components/duration-control/duration-control";
import { MatIconModule } from "@angular/material/icon";
import { DateAdapter } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LangsProvider, Lang } from '../../../common/providers/langs-provider';

@Component({
  selector: 'annual-plan-dialog-form-athlete-plan-new',
  templateUrl: './annual-plan-dialog-form-athlete-plan-new.component.html',
  styleUrls: ['./annual-plan-dialog-form-athlete-plan-new.scss'],
  imports: [
    TranslateModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatInputModule,
    NgIf,
    NgFor,
    MatDialogModule,
    MatIconModule,
    MatLabel,
    MatError,
    MatButton,
    DurationControlComponent,
    MatCheckboxModule
  ],
  standalone: true
})
export class AnnualPlanDialogFormAthletePlanNewComponent implements OnInit, AfterViewInit, OnDestroy {

  public annualPlanForm: FormGroup;
  public isSubmitting = false;
  public annualPlan: AnnualPlan = {} as AnnualPlan;
  public startDate: Date = new Date();
  public duplicateAsModel = false;
  public isLangSelectable = false;
  public skipLangSelection = false;
  public selectedLanguages: number[] = [];
  public availableLangs: Lang[] = [];
  public languagesFormControl: FormControl = new FormControl<number[]>([], [
    (ctrl) => ctrl.value && ctrl.value.length > 0 ? null : { required: true }
  ]);

  public duration = 0;
  private subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialogRef<AnnualPlanDialogFormAthletePlanNewComponent>,
    private utils: UtilsProvider,
    private fb: FormBuilder,
    private toaster: MatSnackBar,
    private annualPlanProvider: AnnualPlanService,
    private langsProvider: LangsProvider,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    // use the FormBuilder to create a form group
    this.annualPlanForm = this.fb.group({
      title: new FormControl('', [Validators.required]),
      startedAt: new FormControl('', [Validators.required]),
      // endedAt: new FormControl('', [Validators.required]),
      weeksLength: new FormControl(52, [Validators.required, Validators.min(16), Validators.max(52)]),
      weeklyWorkingTime: new FormControl('', [Validators.required, Validators.min(1), Validators.max(604800)]),
      comment: new FormControl('', [Validators.maxLength(5000)])
    });
    if (this.data.isLangSelectable !== undefined) {
      this.isLangSelectable = this.data.isLangSelectable;
    }
  }

  public ngOnInit() {
    if (this.data.annualPlanRow) {
      this.annualPlanForm.patchValue(this.data.annualPlanRow);
      this.duration = this.f['weeklyWorkingTime'].value;
    }
    this.duplicateAsModel = !!this.data.duplicateAsModel;
  }

  public ngAfterViewInit() {
    const langId = this.langsProvider.getBestLangId();

    if (this.data.annualPlanRow) {
      this.data.skipLangSelection = this.data.skipLangSelection || false;

      this.selectedLanguages = !this.data.skipLangSelection && this.data.annualPlanRow.availableLangIds || [];
    }

    if (!this.selectedLanguages.includes(langId)) {
      this.selectedLanguages.push(langId);
    }

    this.languagesFormControl.setValue(this.selectedLanguages);

    this.availableLangs = this.langsProvider.getLangs();
    this.cdr.detectChanges();
  }

  public toggleLanguage(langId: number, checked: boolean): void {
    if (checked) {
      this.selectedLanguages.push(langId);
    } else {
      const index = this.selectedLanguages.indexOf(langId);
      this.selectedLanguages.splice(index, 1);
    }

    this.languagesFormControl.setValue(this.selectedLanguages);
  }

  public isLangSelected(langId: number): boolean {
    return this.selectedLanguages.includes(langId);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public submitForm() {
    this.isSubmitting = true;
    this.f['weeklyWorkingTime'].setValue(this.duration);

    if (this.annualPlanForm.valid) {

      // duplicate
      if (this.data.toDuplicate) {

        // update the model
        this.updateAnnualPlan(
          this.annualPlanForm.value,
          {id: this.data.annualPlanRow ? this.data.annualPlanRow.id : null},
          {organization: this.data.organization},
          {athlete: this.data.athlete},
        );
        this.subscriptions.push(
          this.annualPlanProvider.duplicate(this.annualPlan).subscribe(
            (data) => {
              this.dialog.close(data);
            },
            (err) => {
              this.isSubmitting = false;
              this.toaster.open(err.error.message, '', { duration: 5000 });
            }
          )
        );
      } else if (this.duplicateAsModel) {

        // patch for duplicate as model
        this.updateAnnualPlan(
          this.annualPlanForm.value,
          {id: this.data.annualPlanRow ? this.data.annualPlanRow.id : null},
          {organization: this.data.organization},
          {athlete: null},
          {library: this.data.library},
        );
        this.annualPlan.startedAt = undefined;
        this.subscriptions.push(
          this.annualPlanProvider.duplicate(this.annualPlan, this.selectedLanguages).subscribe(
            (data) => {
              this.dialog.close(data);
            },
            (err) => {
              this.isSubmitting = false;
              this.toaster.open(err.error.message, '', { duration: 5000 });
            }
          )
        );
      } else {

        // update the model
        this.updateAnnualPlan(
          this.annualPlanForm.value,
          {id: this.data.annualPlanRow ? this.data.annualPlanRow.id : null},
          {organization: this.data.organization},
          {athlete: this.data.athlete},
        );
        this.subscriptions.push(
          this.annualPlanProvider.save(this.annualPlan).subscribe(
            (data) => {
              this.dialog.close(data);
            },
            (err) => {
              this.isSubmitting = false;
              this.toaster.open(err.error.message, '', {duration: 5000});
            }
          )
        );
      }

      // copy library annual plan as an athlete annual plan
    } else {
      this.utils.validateAllFormFields(this.annualPlanForm);
      this.isSubmitting = false;
    }
  }

  private updateAnnualPlan(...values: Object[]) {
    Object.assign(this.annualPlan, ...values);
    if (typeof this.annualPlan.startedAt === 'object') {
      // je dois transformer la valeur "object" en "string"
      // @ts-ignore si c'est un objet, c'est un "Moment"
      this.annualPlan.startedAt = this.annualPlan.startedAt.format('YYYY-MM-DD HH:mm:ss');
    }
  }

  get f() {
    return this.annualPlanForm.controls;
  }
}
